import React, { useEffect } from 'react'
import { autorun } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import { Box, FormLabel, Grid } from '@material-ui/core'

import { CircleCheckbox } from './CircleCheckbox'
import { ImportProductsProps } from './ImportScreen'

import { ImportAddProductsFunc, ImportUpdateProductsFunc, useImportEvents } from '../hooks/importHooks'

import { ServerTableData } from '../store/serverTableData'

import { EnumConstraint } from '../common/tsUtils'

import { StoreAccountProps, StoreAccountModel } from '../types/accountProps'
import { StoreProductModel } from '../server/mpsklad_core/Models/StoreProductModel'
import { ImportAddOptions } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportAddOptions'
import { ImportUpdateOptions } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportUpdateOptions'

//TODO: Remove temporary prop isDisabled before release
export type ImportProductsFormProps<
  TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never>,
  TRow extends StoreProductModel,
  TColumnId extends string,
  TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown}
> = StoreAccountProps
  & ImportProductsProps<StoreAccountModel, TProducts, TRow, TColumnId, TFilterEnum>
  & {
  addProductsFunc: ImportAddProductsFunc

  updateProductsFunc: ImportUpdateProductsFunc
}

export const ImportProductsForm =
  observer(
    <TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never>,
      TRow extends StoreProductModel,
      TColumnId extends string,
      TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown}>
    ({
       account,
       addProductsFunc,
       updateProductsFunc,
       products
     }: ImportProductsFormProps<TProducts, TRow, TColumnId, TFilterEnum>) => {
      const classes = useStyles()

      const {onProductImport, onProductUpdate, isSubmitting} =
        useImportEvents(account, products)

      const options =
        useLocalObservable<{
          addNewProducts: boolean
          addOptions: ImportAddOptions
          updateOptions: ImportUpdateOptions
        }>(() => ({
          addNewProducts: true,
          addOptions: {
            variantsAsStandalone: true,
            images: true,
            barcodes: true,
            prices: true
          },
          updateOptions: {
            name: true,
            article: true,
            images: true,
            barcodes: true,
            prices: true
          }
        }))

      useEffect(() =>
          autorun(() =>
            // TODO: This conflicts with import stocks, which must show only matched products (not implemented)
            // TODO: Split product tables for import of products and stocks?
            // TODO: Or product import should allow simultaneous adding and updating - then this filter won't be needed
            products.setPropFilterReadOnly('hasMsProduct', true)
                    .setPropFilter('hasMsProduct', JSON.stringify(!options.addNewProducts))),
        [products, options])

      const {addNewProducts, addOptions, updateOptions} = options

      return (
        <Box className={classes.container}>
          <Grid
            container
            direction="column"
            className={clsx(classes.settingsBlock, 'default-border')}
            style={{display: 'flex', flexDirection: 'column'}}
          >
            <Grid item>
              <FormLabel component="legend" className={classes.settingsBlockLabel}>
                Импорт товаров в МойСклад
              </FormLabel>
            </Grid>

            <Grid item container direction="row">
              <Grid item xs={6}>
                <CircleCheckbox
                  label="Добавить новые товары в МойСклад"
                  checked={addNewProducts}
                  onChange={e => options.addNewProducts = e.target.checked}
                />

                {
                  addNewProducts &&
                  <Box display="flex" flexDirection="column" ml={3}>
                    <CircleCheckbox
                      label="Создать модификации товаров отдельными карточками"
                      checked={addOptions.variantsAsStandalone}
                      onChange={e => addOptions.variantsAsStandalone = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Добавить картинки"
                      checked={addOptions.images}
                      onChange={e => addOptions.images = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Добавить баркод"
                      checked={addOptions.barcodes}
                      onChange={e => addOptions.barcodes = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Добавить цену"
                      checked={addOptions.prices}
                      onChange={e => addOptions.prices = e.target.checked}
                    />
                  </Box>
                }
              </Grid>

              <Grid item xs={6}>
                <CircleCheckbox
                  label="Обновить существующие в МоёмСкладе товары"
                  checked={!addNewProducts}
                  onChange={e => options.addNewProducts = !e.target.checked}
                />

                {
                  !addNewProducts &&
                  <Box display="flex" flexDirection="column" ml={3}>
                    <CircleCheckbox
                      label="Обновить наименование"
                      checked={updateOptions.name}
                      onChange={e => updateOptions.name = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Обновить артикул"
                      checked={updateOptions.article}
                      onChange={e => updateOptions.article = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Обновить картинки"
                      checked={updateOptions.images}
                      onChange={e => updateOptions.images = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Обновить баркод"
                      checked={updateOptions.barcodes}
                      onChange={e => updateOptions.barcodes = e.target.checked}
                    />

                    <CircleCheckbox
                      label="Обновить цену"
                      checked={updateOptions.prices}
                      onChange={e => updateOptions.prices = e.target.checked}
                    />
                  </Box>
                }
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={6}>
              {
                addNewProducts &&
                <button
                  type="button"
                  className={clsx(classes.printButton, 'default-button')}
                  onClick={onProductImport(addProductsFunc, addOptions)}
                  disabled={isSubmitting}
                >
                  <p>
                    {isSubmitting ? 'Импортируем...' : 'Импортировать товары'}
                  </p>
                </button>
              }
            </Grid>

            <Grid item xs={6}>
              {
                !addNewProducts &&
                <button
                  type="button"
                  className={clsx(classes.printButton, 'default-button')}
                  onClick={onProductUpdate(updateProductsFunc, updateOptions)}
                  disabled={isSubmitting}
                >
                  <p>
                    {isSubmitting ? 'Обновляем...' : 'Обновить товары'}
                  </p>
                </button>
              }
            </Grid>
          </Grid>
        </Box>
      )
    })

const useStyles = makeStyles(() => ({
  container: {
    padding: '16px 0px 0px 0px',
    width: 'calc(100vw - 350px)'
  },
  settingsBlock: {
    flexWrap: 'unset',
    marginTop: 20,
    marginBottom: 20,
    marginLeft: 0,
    padding: 30,
    borderRadius: 12
  },
  settingsBlockLabel: {
    color: '#1F364D',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 15
  },
  printButton: {
    width: 221,
    height: 40,
    marginTop: 0,
    color: '#FFFFFF',
    alignItems: 'center'
  }
}))